// Unified Auth Store - Combines authentication and demo functionality
// Follows the pattern documented in migration-implementation-guide.md section 1.1

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type User as FirebaseUser } from 'firebase/auth';
import {
  signInWithGoogle,
  signOut as firebaseSignOut,
  onAuthStateChange,
  type UserProfile,
} from '@/lib/auth';
import type { UserRole } from '@monthly-data-collection/shared';
import { getUserProfileEnhancedFallback } from '@/lib/auth-system-users';
import { AvatarCacheService } from '@/domains/billing/services/teamtasks/avatarCacheService';
import { log } from '@/lib/logger';
import {
  resolveUserAccess,
  hasRouteAccess as checkRouteAccess,
  hasCapability as checkCapability,
  type ResolvedAccess,
} from '@/lib/group-access';
import type { SystemUserWithEmployeeData } from '@/shared/hooks/useSystemUsers';
import type { Capability } from '@monthly-data-collection/shared';

// Extended user profile for component compatibility
interface ExtendedUserProfile extends UserProfile {
  displayName: string; // Alias for name for backward compatibility
}

// Demo user profile (matches DemoContext's DemoUserProfile)
interface DemoUserProfile {
  uid: string;
  employee_id: number;
  email: string;
  name: string;
  team_name: string;
  role_name: string;
  role: UserRole;
  lastActive: Date;
  displayName: string;
  avatar?: string;
  cached_avatar_url?: string;
  cached_thumbnail_url?: string;
  avatar_cache_status?: 'pending' | 'cached' | 'failed';
  avatar_cache_timestamp?: Date;
}

// Unified auth state interface
interface AuthState {
  // Authentication state
  user: FirebaseUser | null;
  userProfile: ExtendedUserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  
  // Demo state (previously in DemoContext)
  isDemoMode: boolean;
  demoEmployee: SystemUserWithEmployeeData | null;
  availableEmployees: SystemUserWithEmployeeData[];
  isDevEnvironment: boolean;
  isDemoTransitioning: boolean; // NEW: Track demo mode transitions
  
  // Computed effective user (resolves demo vs real user)
  effectiveUser: ExtendedUserProfile | null;

  // Group-based access
  groupAccess: ResolvedAccess | null;

  // Enhanced readiness state for preventing race conditions
  isFullyReady: boolean; // True when auth is complete and demo transition (if any) is done

  // Authentication actions
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;

  // Demo actions
  switchToDemo: (employee: SystemUserWithEmployeeData) => void;
  exitDemo: () => void;
  setAvailableEmployees: (employees: SystemUserWithEmployeeData[]) => void;

  // Group-based permission helpers
  hasGroupRouteAccess: (path: string) => boolean;
  hasGroupCapability: (cap: Capability) => boolean;

  // Internal actions
  _setUser: (user: FirebaseUser | null) => void;
  _setUserProfile: (profile: ExtendedUserProfile | null) => void;
  _setLoading: (loading: boolean) => void;
  _setError: (error: string | null) => void;
}

// Helper function to convert SystemUser to DemoUserProfile
const convertToDemoProfile = (employee: SystemUserWithEmployeeData): DemoUserProfile => {
  // Convert Firestore Timestamp to Date if needed
  const convertTimestamp = (timestamp: any): Date | undefined => {
    if (!timestamp) return undefined;
    if (timestamp instanceof Date) return timestamp;
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    }
    return undefined;
  };

  return {
    uid: `demo-${employee.id}`,
    employee_id: employee.employee_id,
    email: employee.email,
    name: employee.employee_data?.name || 'Unknown Employee',
    team_name: employee.employee_data?.team_name || 'Unknown Team',
    role_name: employee.employee_data?.role_name || 'Unknown Role',
    role: (employee.role || 'contributor') as UserRole,
    lastActive: new Date(),
    displayName: employee.employee_data?.name || 'Unknown Employee',
    avatar: employee.employee_data?.avatar,
    cached_avatar_url: employee.employee_data?.cached_avatar_url,
    cached_thumbnail_url: employee.employee_data?.cached_thumbnail_url,
    avatar_cache_status: employee.employee_data?.avatar_cache_status,
    avatar_cache_timestamp: convertTimestamp(employee.employee_data?.avatar_cache_timestamp),
  };
};

// Create the unified auth store
export const useAuthStore = create<AuthState>()(
  devtools(
    (set, get) => ({
      // Initial authentication state
      user: null,
      userProfile: null,
      isLoading: true,
      isAuthenticated: false,
      error: null,
      
      // Initial demo state
      isDemoMode: false,
      demoEmployee: null,
      availableEmployees: [],
      isDevEnvironment: false,
      isDemoTransitioning: false,
      
      // Initial computed state
      effectiveUser: null,
      groupAccess: null,
      isFullyReady: false,
      
      // Authentication actions
      signIn: async () => {
        set({ isLoading: true, error: null });
        try {
          await signInWithGoogle();
          // User state will be updated by the auth state listener
        } catch (err) {
          log.error('Sign in error:', err);
          set({
            error: err instanceof Error ? err.message : 'An error occurred during sign in. Please try again.',
            isLoading: false,
          });
        }
      },
      
      signOut: async () => {
        set({ isLoading: true, error: null });
        try {
          await firebaseSignOut();
          // User state will be updated by the auth state listener
        } catch (err) {
          log.error('Sign out error:', err);
          set({
            error: err instanceof Error ? err.message : 'An error occurred during sign out. Please try again.',
            isLoading: false,
          });
        }
      },
      
      clearError: () => set({ error: null }),
      
      // Demo actions with transition state management
      switchToDemo: (employee: SystemUserWithEmployeeData) => {
        const { isDevEnvironment } = get();
        if (!isDevEnvironment) {
          log.warn('Demo mode is only available in development environment or when explicitly enabled');
          return;
        }

        log.info(`[AUTH-STORE] Switching to demo mode for employee: ${employee.employee_id}`);

        // Set transitioning state to prevent race conditions
        set({ isDemoTransitioning: true, isFullyReady: false });

        const demoProfile = convertToDemoProfile(employee);

        // Resolve group access for the demo user (same logic as real login)
        resolveUserAccess(employee.groups, employee.isSuperAdmin)
          .then((access) => {
            setTimeout(() => {
              set({
                isDemoMode: true,
                demoEmployee: employee,
                effectiveUser: demoProfile,
                groupAccess: access,
                isDemoTransitioning: false,
                isFullyReady: true,
              });
              log.info(`[AUTH-STORE] Demo mode transition completed for employee: ${employee.employee_id}`, {
                groupIds: access.groupIds,
                isSuperAdmin: access.isSuperAdmin,
                routeCount: access.allowedRoutes.size,
              });
            }, 100);
          })
          .catch((err) => {
            log.warn(`[AUTH-STORE] Failed to resolve demo user group access:`, err);
            // Fall back to demo without group access
            setTimeout(() => {
              set({
                isDemoMode: true,
                demoEmployee: employee,
                effectiveUser: demoProfile,
                groupAccess: null,
                isDemoTransitioning: false,
                isFullyReady: true,
              });
            }, 100);
          });
      },
      
      exitDemo: () => {
        const { userProfile } = get();

        log.info(`[AUTH-STORE] Exiting demo mode`);

        // Set transitioning state
        set({ isDemoTransitioning: true, isFullyReady: false });

        // Restore real user's group access
        resolveUserAccess(userProfile?.groups, userProfile?.isSuperAdmin)
          .then((access) => {
            setTimeout(() => {
              set({
                isDemoMode: false,
                demoEmployee: null,
                effectiveUser: userProfile,
                groupAccess: access,
                isDemoTransitioning: false,
                isFullyReady: true,
              });
              log.info(`[AUTH-STORE] Demo mode exit completed`);
            }, 100);
          })
          .catch(() => {
            // Fall back without group re-resolution
            setTimeout(() => {
              set({
                isDemoMode: false,
                demoEmployee: null,
                effectiveUser: userProfile,
                isDemoTransitioning: false,
                isFullyReady: true,
              });
            }, 100);
          });
      },
      
      setAvailableEmployees: (employees: SystemUserWithEmployeeData[]) => {
        set({ availableEmployees: employees });
      },
      
      // Group-based permission helpers
      hasGroupRouteAccess: (path: string): boolean => {
        // Routes accessible to all authenticated users (no group required)
        if (path === '/home') return true;
        const { groupAccess } = get();
        if (!groupAccess) return false;
        return checkRouteAccess(path, groupAccess);
      },

      hasGroupCapability: (cap: Capability): boolean => {
        const { groupAccess } = get();
        if (!groupAccess) return false;
        return checkCapability(cap, groupAccess);
      },

      // Internal actions (used by auth state listener)
      _setUser: (user: FirebaseUser | null) => {
        set({ user, isAuthenticated: !!user });
      },
      
      _setUserProfile: (profile: ExtendedUserProfile | null) => {
        const { isDemoMode, isDemoTransitioning } = get();
        set({
          userProfile: profile,
          // Update effective user only if not in demo mode
          effectiveUser: isDemoMode ? get().effectiveUser : profile,
          // Set fully ready when profile is loaded and not transitioning
          isFullyReady: !!profile && !isDemoTransitioning,
        });
      },
      
      _setLoading: (loading: boolean) => {
        const { isDemoTransitioning } = get();
        set({
          isLoading: loading,
          // Only set fully ready if not loading and not transitioning
          isFullyReady: !loading && !isDemoTransitioning && !!get().effectiveUser,
        });
      },
      
      _setError: (error: string | null) => {
        set({ error });
      },
    }),
    { name: 'auth-store' }
  )
);

// Initialize auth state listener
let authUnsubscribe: (() => void) | null = null;

export const initializeAuthStore = () => {
  if (authUnsubscribe) {
    return; // Already initialized
  }
  
  // Check if we're in development environment
  const isDev = import.meta.env.DEV ||
                import.meta.env.MODE === 'development' ||
                process.env.NODE_ENV === 'development' ||
                import.meta.env.VITE_ENABLE_DEMO_MODE === 'true';
  
  useAuthStore.setState({ isDevEnvironment: isDev });

  // Set up auth state listener
  authUnsubscribe = onAuthStateChange(async (currentUser) => {
    const { _setUser, _setLoading, _setError, _setUserProfile } = useAuthStore.getState();
    
    _setUser(currentUser);
    _setLoading(true);
    
    try {
      if (currentUser) {
        log.debug(`[AUTH-STORE] Loading user profile for Firebase UID: ${currentUser.uid}, email: ${currentUser.email}`);
        
        const profile = await getUserProfileEnhancedFallback(
          currentUser.uid,
          currentUser.email || undefined,
          currentUser.displayName || undefined
        );
        
        if (profile) {
          log.debug(`[AUTH-STORE] Successfully loaded user profile:`, {
            uid: profile.uid,
            employee_id: profile.employee_id,
            email: profile.email,
            name: profile.name,
            role: profile.role,
          });
          
          const extendedProfile: ExtendedUserProfile = {
            ...profile,
            displayName: profile.name,
          };

          _setUserProfile(extendedProfile);

          // Resolve group-based access (new permissions system)
          // Skip if in demo mode — demo user's groupAccess is set by enterDemoMode
          try {
            const access = await resolveUserAccess(profile.groups, profile.isSuperAdmin);
            const { isDemoMode } = useAuthStore.getState();
            if (!isDemoMode) {
              useAuthStore.setState({ groupAccess: access });
              log.debug(`[AUTH-STORE] Group access resolved:`, {
                groupIds: access.groupIds,
                isSuperAdmin: access.isSuperAdmin,
                routeCount: access.allowedRoutes.size,
                capabilityCount: access.capabilities.size,
              });
            } else {
              log.debug(`[AUTH-STORE] Skipped groupAccess update (demo mode active)`);
            }
          } catch (groupErr) {
            log.warn(`[AUTH-STORE] Failed to resolve group access (falling back to role-based):`, groupErr);
          }
          
          // Trigger avatar caching asynchronously
          if (profile.employee_id && profile.avatar) {
            AvatarCacheService.cacheAvatarIfNeeded(
              profile.employee_id.toString(),
              profile.avatar,
              profile.cached_avatar_url,
              profile.avatar_cache_status,
              profile.avatar_cache_timestamp,
              24
            ).catch(error => {
              log.warn(`[AUTH-STORE] Avatar caching failed for employee ${profile.employee_id}:`, error);
            });
          }
        } else {
          log.warn(`[AUTH-STORE] No user profile found for Firebase UID: ${currentUser.uid}, email: ${currentUser.email}`);
          _setUserProfile(null);
          _setError('User profile not found. Please contact your administrator to set up your account.');
        }
      } else {
        log.debug(`[AUTH-STORE] User signed out, clearing profile`);
        _setUserProfile(null);
        useAuthStore.setState({ groupAccess: null });
      }
    } catch (err) {
      log.error(`[AUTH-STORE] Error in auth state change:`, err);
      _setError('Failed to load user profile. Please try refreshing the page.');
      _setUserProfile(null);
    } finally {
      _setLoading(false);
    }
  });
};

// Cleanup function
export const cleanupAuthStore = () => {
  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = null;
  }
};

